/**
 * BuyGamesModal — модалка выбора и покупки пакета игр.
 * Сейчас работает только для VK Mini App (через VK Голоса).
 * Telegram-юзеры покупают через бота, отдельно.
 */
import { useState } from 'react';
import { isVK } from '../platform/detect';
import { VK_GAME_PACKAGES, purchaseWithVotes } from '../lib/vk-votes';
import { usePurchaseStore } from '../stores/purchase';

export function BuyGamesModal() {
  const { isOpen, close, refetchBalance } = usePurchaseStore();
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  if (!isOpen) return null;

  const onBuy = async (pkgId: string) => {
    if (!isVK()) {
      setError('Покупка через ВК-голоса доступна только в VK Mini App.');
      return;
    }
    setPendingId(pkgId);
    setError(null);
    setSuccess(null);

    const result = await purchaseWithVotes(pkgId);
    setPendingId(null);

    if (result.success) {
      const pkg = VK_GAME_PACKAGES.find((p) => p.id === pkgId);
      setSuccess(`Готово! Начислено ${pkg?.games ?? 0} игр.`);
      refetchBalance?.();
      setTimeout(() => {
        setSuccess(null);
        close();
      }, 1500);
    } else if (result.error === 'cancelled') {
      // Пользователь сам отменил — модалку оставляем открытой
    } else {
      setError(result.error || 'Ошибка оплаты');
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={close}
    >
      <div
        className="bg-gradient-to-br from-indigo-900 to-purple-900 w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl p-6 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-white">🎮 Купить игры</h2>
          <button
            onClick={close}
            className="text-white/60 hover:text-white text-2xl leading-none"
            aria-label="Закрыть"
          >
            ×
          </button>
        </div>

        <p className="text-purple-200 text-sm mb-5">
          Игры тратятся на запуск викторин в комнатах. Бесплатные — 4 на старт.
        </p>

        <div className="space-y-3">
          {VK_GAME_PACKAGES.map((pkg) => (
            <button
              key={pkg.id}
              onClick={() => onBuy(pkg.id)}
              disabled={pendingId !== null}
              className={`w-full p-4 rounded-xl text-left transition-all border ${
                pkg.popular
                  ? 'bg-gradient-to-r from-amber-500/20 to-orange-500/20 border-amber-400/60'
                  : 'bg-white/10 border-white/20'
              } hover:scale-[1.02] disabled:opacity-50 disabled:cursor-wait`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-white font-bold flex items-center gap-2">
                    {pkg.title}
                    {pkg.popular && (
                      <span className="text-xs bg-amber-500 text-black px-2 py-0.5 rounded-full font-bold">
                        ХИТ
                      </span>
                    )}
                  </div>
                  <div className="text-purple-200 text-sm mt-1">
                    {pkg.games} игр • {pkg.votes} голосов
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-white font-bold">≈ {pkg.priceRub}₽</div>
                  {pendingId === pkg.id && (
                    <div className="text-xs text-purple-300 mt-1">оплата…</div>
                  )}
                </div>
              </div>
            </button>
          ))}
        </div>

        {error && (
          <div className="mt-4 p-3 bg-red-500/20 border border-red-400/40 rounded-lg text-red-200 text-sm">
            ❌ {error}
          </div>
        )}

        {success && (
          <div className="mt-4 p-3 bg-green-500/20 border border-green-400/40 rounded-lg text-green-200 text-sm">
            ✅ {success}
          </div>
        )}

        <p className="text-purple-400 text-xs mt-5 text-center">
          Оплата через VK Голоса. Игры начисляются мгновенно.
        </p>
      </div>
    </div>
  );
}
